import { useEffect, useMemo, useState } from "react";
import { buildCommandData, CommandAction } from "../../lib/commandCore";
import { getCachedAdvice, missionAdviceKey, setCachedAdvice, type MissionAdvice } from "../../lib/missionAdviceCache";
import { todayLocalKey } from "../../lib/delegationCore";
import { api, type HeadlineTask, type Task } from "../../lib/tauri";
import { useSyncStore } from "../../stores/syncStore";
import { useTaskStore } from "../../stores/taskStore";
import { useUiStore } from "../../stores/uiStore";
import { useProjectStore } from "../../stores/projectStore";
import { AiClassifyPanel } from "../AiClassifyPanel";
import { AgentBoard } from "./AgentBoard";
import { CommandBar } from "./CommandBar";
import { CommandSheet } from "./CommandSheet";
import { DailyKnowledgeCard } from "./DailyKnowledgeCard";
import { DecisionDrawer } from "./DecisionDrawer";
import { OperationHeader } from "./OperationHeader";
import { PrimaryMissions } from "./PrimaryMissions";
import { SignalStrip } from "./SignalStrip";
import { StrategyCard } from "./StrategyCard";
import { TodayBrief } from "./TodayBrief";

interface Props {
  onMenuClick?: () => void;
}

// 「今日の作戦」AI大見出しの当日キャッシュ（アプリ稼働中のみ・永続化しない）。
// 司令塔を開くたびに gemma を叩かない/見出しが変わらないための module-level 保持。
// 同一プロセス内で当日 generate 済みなら再生成も再呼び出しもしない（Rust DB 側にも当日
// キャッシュがあり、別プロセス/再起動でもそこが効く）。値が "" の日は当日生成を試みない
// （= 生成失敗 or タスク無し。rule v0 をそのまま使う）。
const headlineByDay = new Map<string, string>();

export function CommandCenter({ onMenuClick }: Props) {
  const allTasks = useTaskStore((s) => s.allTasks);
  const stats = useTaskStore((s) => s.stats);
  const syncStatus = useSyncStore((s) => s.status);
  const settings = useSyncStore((s) => s.settings);
  const setEditorOpen = useUiStore((s) => s.setEditorOpen);
  const setVoiceOpen = useUiStore((s) => s.setVoiceOpen);
  const setAppView = useUiStore((s) => s.setAppView);
  const showToast = useUiStore((s) => s.showToast);
  const [selectedAction, setSelectedAction] = useState<CommandAction | null>(null);
  const projects = useProjectStore((s) => s.projects);
  const [advice, setAdvice] = useState<MissionAdvice>({});
  const [adviceLoading, setAdviceLoading] = useState(false);
  // AI生成の「今日の作戦」大見出し（空 = 未取得/失敗 → rule v0 を使う）
  const [aiHeadline, setAiHeadline] = useState("");

  const activeTasks = useMemo(
    () => allTasks.filter((t) => !t.completed && !t.deleted),
    [allTasks]
  );

  // 司令塔マウント時に「当日キャッシュ無ければ静かに1回だけ」AI大見出しを生成。
  // 仕事(work)も生活(life)も全部の未完了タスク + KPI(Rust側で取得)から1手を選ぶ。
  // 失敗(Ollama 停止/非Tauri/パース失敗)は静かに無視 → rule v0 の見出しが出る。
  useEffect(() => {
    const day = todayLocalKey();
    const cached = headlineByDay.get(day);
    if (cached !== undefined) {
      // 当日処理済み（生成成功で見出し記録 or 生成は成功したが空 "" を記録）。再呼び出ししない。
      setAiHeadline(cached);
      return;
    }
    if (activeTasks.length === 0) return; // タスクが揃う前は待つ（記録もしない=後で再試行可）

    const projectById = new Map(projects.map((p) => [p.id, p]));
    const headlineTasks: HeadlineTask[] = activeTasks.map((t) => {
      const proj = t.projectId ? projectById.get(t.projectId) : undefined;
      return {
        title: t.title,
        priority: t.priority,
        dueDate: t.dueDate ?? null,
        project: proj?.name ?? "",
        kind: proj?.kind ?? "",
      };
    });

    let cancelled = false;
    void (async () => {
      try {
        const result = await api.aiSuggestHeadline?.(headlineTasks);
        const headline = result?.headline?.trim() ?? "";
        if (cancelled) return;
        // 成功時のみ当日キャッシュに刻む（失敗時は未記録のまま → 次マウントで再試行可）
        headlineByDay.set(day, headline);
        setAiHeadline(headline);
      } catch {
        // gemma 停止/非Tauri環境でも司令塔は rule v0 で完結（静かに無視）
      }
    })();
    return () => {
      cancelled = true;
    };
    // activeTasks の有無で1回起動できればよい。projects は同期的に揃う前提で依存に含める。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTasks.length === 0, projects]);

  const data = useMemo(
    () => buildCommandData({ tasks: allTasks, stats, syncStatus, settings }),
    [allTasks, stats, syncStatus, settings]
  );

  // rule v0 ミッション（fallback除く）に対応する Task 実体。セレクタ内でなく useMemo で派生（Zustand v5）
  const missionTasks = useMemo(
    () =>
      data.missions
        .filter((m) => !m.isFallback)
        .map((m) => allTasks.find((t) => t.id === m.id))
        .filter((t): t is Task => Boolean(t)),
    [data.missions, allTasks]
  );

  // マウント/ミッション変化時はキャッシュ読みのみ（gemma を呼ばない — 完了条件5）
  useEffect(() => {
    const cached = getCachedAdvice(missionAdviceKey(missionTasks));
    setAdvice(cached ?? {});
  }, [missionTasks]);

  // 「AI参考」ボタン起点のみ gemma を呼ぶ。結果は参考表示で rule v0 の並び・owner を上書きしない
  const refreshAdvice = async () => {
    if (missionTasks.length === 0) return;
    const key = missionAdviceKey(missionTasks);
    const cached = getCachedAdvice(key);
    if (cached) {
      setAdvice(cached);
      return;
    }
    setAdviceLoading(true);
    try {
      const briefs = projects
        .filter((p) => !p.archived)
        .map((p) => ({ id: p.id, name: p.name, kind: p.kind }));
      const result = (await api.aiClassifyInbox?.(missionTasks, briefs)) ?? [];
      const next: MissionAdvice = Object.fromEntries(result.map((s) => [s.taskId, s]));
      setCachedAdvice(key, next);
      setAdvice(next);
      if (result.length === 0) showToast("AI参考なし。rule v0 の並びをそのまま使えます");
    } catch {
      // gemma 停止時もルールベース/手動で完結（完了条件4）
      showToast("ローカルAI(Ollama)に接続できません。rule v0 の並びはそのまま使えます");
    } finally {
      setAdviceLoading(false);
    }
  };

  const handleChoose = (action: CommandAction) => {
    setSelectedAction(action);
    const mission = data.missions.find((m) => !m.isFallback);
    void api
      .addDecision?.({
        missionId: mission?.id ?? "",
        action,
        reason: mission
          ? `GO ${action}: ${mission.title} (score ${mission.score})`
          : `GO ${action}: ミッションなし`,
      })
      .catch((error) => {
        console.error("[Flow] addDecision failed", error);
        showToast("判断ログの保存に失敗しました");
      });
  };
  const realMissionCount = data.missions.filter((mission) => !mission.isFallback).length;
  const primaryMission = data.missions[0] ?? null;

  return (
    <div className="command-shell flex h-full min-h-0 flex-col">
      <OperationHeader
        operationId={data.operationId}
        title={aiHeadline || data.operationTitle}
        missionCount={realMissionCount}
        onCreateTask={() => setEditorOpen(true)}
        onVoiceTask={() => setVoiceOpen(true)}
        onShowTasks={() => setAppView("tasks")}
        onMenuClick={onMenuClick}
      />
      <div className="flow-scrollbar min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-5 md:px-8">
          <TodayBrief
            brief={data.todayBrief}
            nextAction={data.nextAction}
            nextActionLabel={data.nextActionLabel}
          />
          {/* 今日のナレッジ（usefulness高×古い/未使用を1件。空なら自動で非表示） */}
          <DailyKnowledgeCard />
          <PrimaryMissions
            missions={data.missions}
            advice={advice}
            adviceLoading={adviceLoading}
            onRefreshAdvice={() => void refreshAdvice()}
            onOpenMission={(id) => {
              const mission = data.missions.find((item) => item.id === id);
              if (mission?.isFallback) {
                setSelectedAction("B");
              } else if (mission) {
                setEditorOpen(true, id);
              }
            }}
          />
          <details className="group">
            <summary className="command-soft-control flex h-11 cursor-pointer list-none items-center justify-between px-4 text-sm font-semibold marker:hidden">
              <span>補助ツール</span>
              <span className="command-faint text-xs transition group-open:rotate-180">⌄</span>
            </summary>
            <div className="mt-4 flex flex-col gap-5">
              <StrategyCard missions={data.missions} />
              <AiClassifyPanel activeTasks={activeTasks} />
              <SignalStrip signals={data.signals} />
              <DecisionDrawer decisions={data.decisions} />
              <AgentBoard squad={data.squad} />
            </div>
          </details>
        </div>
      </div>
      <CommandBar
        suggestedAction={data.nextAction}
        onChoose={handleChoose}
        disabled={selectedAction !== null}
      />
      <CommandSheet
        open={selectedAction !== null}
        action={selectedAction}
        mission={primaryMission}
        onClose={() => setSelectedAction(null)}
      />
    </div>
  );
}
